// Focus management for modal surfaces (Dialog). Kept free of React so the
// wrap-around rule can be tested without a DOM.

export const FOCUSABLE_SELECTOR = [
  'a[href]',
  'area[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'iframe',
  '[contenteditable="true"]',
  '[tabindex]:not([tabindex="-1"])',
].join(',')

/**
 * Where Tab should land when it would leave the trapped region, or null when
 * the browser's own move stays inside it.
 *
 * `current` is the index of the focused element among the focusables, -1 when
 * focus is not on any of them (it sits on the panel itself, or nowhere).
 */
export function trapTarget(count: number, current: number, backwards: boolean): number | null {
  if (count === 0) return null
  if (current === -1) return backwards ? count - 1 : 0
  if (backwards && current === 0) return count - 1
  if (!backwards && current === count - 1) return 0
  return null
}

interface Candidate {
  hasAttribute: (name: string) => boolean
}

/**
 * The element a dialog should focus when it opens: an explicit
 * `data-autofocus` wins, then the first control that is not dialog chrome (the
 * close X and the help icon carry `data-dialog-chrome`), then the chrome
 * itself. Returns the index, or -1 to focus the panel.
 */
export function initialFocusIndex(candidates: Candidate[]): number {
  const explicit = candidates.findIndex((el) => el.hasAttribute('data-autofocus'))
  if (explicit >= 0) return explicit
  const content = candidates.findIndex((el) => !el.hasAttribute('data-dialog-chrome'))
  if (content >= 0) return content
  return candidates.length > 0 ? 0 : -1
}

export function focusableWithin(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (el) => !el.closest('[inert]') && el.getAttribute('aria-hidden') !== 'true',
  )
}

/**
 * The element focus goes back to when a dialog closes. It is captured during
 * render on the closed-to-open transition, before the dialog's children
 * commit: React applies `autoFocus` in the commit's layout phase, which runs
 * earlier than any effect, so reading `document.activeElement` in an effect
 * would record the autofocused field instead of the opener.
 */
export function nextOpener<T>(
  wasOpen: boolean,
  open: boolean,
  current: T | null,
  active: T | null,
): T | null {
  if (open && !wasOpen) return active
  return current
}

interface FocusTarget {
  isConnected: boolean
  focus?: unknown
}

/** The opener when it can still take focus, else null (it left the page). */
export function focusReturnTarget<T extends FocusTarget>(opener: T | null): T | null {
  if (!opener || !opener.isConnected || typeof opener.focus !== 'function') return null
  return opener
}
